import { getWeWorkLeadMetadataFromCookies } from '@wework/www-form-utils';
import { varsFromServer } from './client_side_injection_vars';
import { geoData } from './geo';

// Valid track event names
// analytics.track(EVENT_NAME)
export const EventNameTypes = {
  form: 'Form Submission',
  interaction: 'Interaction',
  page: 'Loaded a Page',
  bucket: 'Bucket',
  timing: 'Timing',
  signupActivity: 'Signup Activity',
  chatActivity: 'Chat Activity',
  availabilityPayload: 'Availability Payload',
  checkout3ds: '3DS Checkout'
};

// Google Tag Manager window.dataLayer Event names
export const DataLayerEventTypes = {
  CHECKOUT_PAYMENT_LEGACY: 'commons signup',
  CHECKOUT_PAYMENT: 'checkout payment complete',
  LEAD_SUBMISSION: 'lead submitted'
};

export function getSegmentAnonymousId() {
  const anonId = window.localStorage.ajs_anonymous_id;
  if (!anonId || typeof anonId !== 'string') {
    return '';
  }
  return JSON.parse(anonId);
}

export function makeSegmentTrackCall(event, properties) {
  const { analyticsGlobals = {}, location = {} } = window;

  const eventProperties = {
    path: location.pathname,
    ...properties,
    userId: analyticsGlobals.userId,
    request_id: analyticsGlobals.requestId,
    app_version: analyticsGlobals.appVersion,
    experiments: varsFromServer.experimentsString,
    app_name: 'vanilla',
    url: location.href,
    heapUserId: properties.heapUserId || (global.GTMheapId && global.GTMheapId.userId),
    segment_anonymous_id: getSegmentAnonymousId()
  };

  if (global.analytics) {
    global.analytics.track(event, eventProperties, {
      integrations: {
        Heap: true
      }
    });
  }
}

export function triggerDataLayer(events) {
  global.analytics.ready(() => {
    window.dataLayer.push(...events);
  });
}

export function trackInteraction(properties) {
  makeSegmentTrackCall(EventNameTypes.interaction, properties);
}

export function setupScrollAnalyticsTracking({ label, selector }) {
  const target = document.querySelector(selector);
  if (!target) {
    return;
  }
  let isTrackedEventSent = false;

  const observer = new IntersectionObserver((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && !isTrackedEventSent) {
      trackInteraction({ label });
      isTrackedEventSent = true;
    }
  });

  observer.observe(target);
}

export function setupScrollAnalyticsTrackingBelowElement({ label, selector }) {
  const target = document.querySelector(selector);
  if (!target) {
    return;
  }
  let didSendTrackEvent = false;

  const observer = new IntersectionObserver((entries) => {
    const [{ isIntersecting, boundingClientRect }] = entries;
    if (!isIntersecting && !didSendTrackEvent && boundingClientRect.bottom < 0) {
      trackInteraction({ label });
      didSendTrackEvent = true;
    }
  });

  observer.observe(target);
}

export function getGoogleAnalyticsData() {
  return new Promise((resolve, reject) => {
    try {
      const { analyticsGlobals = {} } = window;
      const { google_analytics: googleAnalytics = {} } = getWeWorkLeadMetadataFromCookies();

      if (!googleAnalytics.gauserid) {
        googleAnalytics.gauserid = analyticsGlobals.userId;
      }

      if ((!googleAnalytics.gaclientid || !googleAnalytics.gatrackid) && window.ga) {
        // schedule callback function execution when GA library is loaded and ready
        return window.ga((defaultTracker) => {
          const tracker = defaultTracker || window.ga.getAll()[0];
          googleAnalytics.gaclientid = tracker.get('clientId');
          googleAnalytics.gatrackid = tracker.get('trackingId');
          resolve(googleAnalytics);
        });
      }

      resolve(googleAnalytics);
    } catch (err) {
      reject(err);
    }
  });
}

function isValidScrollDistanceTracker({ breakpoint, label }) {
  return breakpoint >= 0 && breakpoint <= 100 && typeof label === 'string';
}

function getDocumentScrollPercentage() {
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
  const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
  const offset = document.documentElement.clientHeight;
  return Math.round((scrollTop / (scrollHeight - offset)) * 100);
}

export function setupScrollDistanceAnalyticsTracking(trackers, callback = trackInteraction) {
  if (!Array.isArray(trackers) || trackers.length === 0) {
    throw new TypeError('Function setupScrollDistanceAnalyticsTracking only accepts non-empty arrays');
  }

  if (trackers.some((tracker) => !isValidScrollDistanceTracker(tracker))) {
    throw new TypeError('Invalid setupScrollDistanceAnalyticsTracking arguments');
  }

  const scrollTrackers = trackers.map((tracker) => ({ ...tracker })); // full copy
  let isScrollTicking = false;
  let currentScrollPercentage = 0;

  window.addEventListener('scroll', () => {
    currentScrollPercentage = getDocumentScrollPercentage();
    if (isScrollTicking) return undefined;

    window.requestAnimationFrame(() => {
      let tracker = null;

      for (let index = scrollTrackers.length - 1; index >= 0; --index) {
        tracker = scrollTrackers[index];
        if (currentScrollPercentage >= tracker.breakpoint) {
          callback({ label: tracker.label });
          scrollTrackers.splice(index, 1);
        }
      }

      isScrollTicking = false;
    });

    isScrollTicking = true;
  });
}

export async function sendCountryCodeToGtm() {
  const { countryCode } = await geoData;
  triggerDataLayer([{ event: 'Geo Data', 'Geo - Country Code': countryCode }]);
}

export const trackAnalyticsInteractionByLink = (link, moduleName = '') => {
  trackInteraction({ label: `Click - ${moduleName} - ${link.textContent.trim()} - Link` });
};
